import React from "react";
import { useState } from "react";
import { Alert } from "@mui/material";
import { LoadingSpinner } from "../LoadingSpinner";
import { BudgetTable } from "./BudgetTable";
import { BudgetHeader } from "./BudgetHeader";
import { BudgetActions } from "./BudgetActions";
import { BudgetRow } from "./types";
import { ApiBudget } from "@/components/budget/types";
import { exportBudgetRowToXLSX } from "../../utils/export";
import { ChangeCounter } from "./ChangeCounter";
import { AddBudgetRowDialog } from "@/components/budget/AddBudgetRowDialog";

interface BudgetContentProps {
  isLoading: boolean;
  isError: boolean;
  isSaving: boolean;
  rows: BudgetRow[];
  months: string[];
  changedFields: Set<string>;
  onAmountChange: (rowId: string, month: string, value: string) => void;
  onAddRow: (accountName: string, accountNumber: number) => void;
  onDeleteRow: (rowId: string) => void;
  onClearRow: (rowId: string) => void;
  hasChanges: boolean;
  onDiscard: () => void;
  onSave: () => void;
  budgetData?: ApiBudget;
}

export const BudgetContent: React.FC<BudgetContentProps> = ({
  isLoading,
  isError,
  isSaving,
  rows,
  months,
  changedFields,
  onAmountChange,
  onAddRow,
  onDeleteRow,
  onClearRow,
  hasChanges,
  onDiscard,
  onSave,
  budgetData,
}) => {


  const [isAddBudgetRowDialogOpen, setIsAddBudgetRowDialogOpen] = useState(false);

  const handleExport = () => {
    const exportRows = rows.filter((row) => !row.isGroupTotal); // Filter out group totals
    exportBudgetRowToXLSX(exportRows, months, budgetData?.name ?? "");
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-[80vh]">
        <LoadingSpinner message={"Loading budget data..."} />
      </div>
    );
  }

  if (isError) {
    <Alert severity="error" className="max-w-md">
      Error while loading the budget data. Please try again later...
    </Alert>
  }

  return (
    <>
      {isSaving && (
        <div className="fixed top-0 left-0 w-full h-screen bg-gray-500/50 flex justify-center items-center z-50">
          <LoadingSpinner message={"Saving changes..."} />
        </div>
      )}
      <div className="flex justify-between items-center">
        <BudgetHeader
          period={budgetData ? {
            yearFrom: budgetData.yearFrom,
            monthFrom: budgetData.monthFrom,
            yearTo: budgetData.yearTo,
            monthTo: budgetData.monthTo,
          } : undefined}
        />
        <BudgetActions
          hasChanges={hasChanges}
          onDiscard={onDiscard}
          onSave={onSave}
          onExport={handleExport}
          onAddBudgetRow={() => setIsAddBudgetRowDialogOpen(true)}
        />
      </div>
      {hasChanges && (
        <div className="flex justify-end mt-2">
          <ChangeCounter count={changedFields.size} />
        </div>
      )}
      <div className="h-5"></div>
      <BudgetTable
        rows={rows}
        months={months}
        changedFields={changedFields}
        onAmountChange={onAmountChange}
        onDeleteRow={onDeleteRow}
        onClearRow={onClearRow}
      />
      <AddBudgetRowDialog
        open={isAddBudgetRowDialogOpen}
        onOpenChange={setIsAddBudgetRowDialogOpen}
        onAddRow={onAddRow}
      />
    </>
  );
};